// Pure, framework-free targeting logic for the incident banner. Everything here
// is unit-testable in isolation (no React, no fetch). The hook fetches
// Statuspage's summary.json and hands the parsed payload to computeIncidentState
// along with this deployment's component allowlist; the result is the same
// IncidentState shape the banner has always consumed.
//
// IncidentIndicator lives here (the dependency-free module) rather than in the
// hook so the import graph stays one-directional (hook → targeting). Defining it
// in the hook and importing it back here created an init-order cycle where this
// module's lookup tables evaluated before the enum existed.

export enum IncidentIndicator {
  NONE = "none",
  MAINTENANCE = "maintenance",
  MINOR = "minor",
  MAJOR = "major",
  CRITICAL = "critical",
}

/** Statuspage impact levels as they appear in summary.json incidents/maintenances. */
export type IncidentImpact =
  | "none"
  | "minor"
  | "major"
  | "critical"
  | "maintenance";

// summary.json components carry more fields than we read (created_at,
// updated_at, position, showcase, start_date, page_id, only_show_if_degraded,
// …). We intentionally subset to what the banner needs — structural typing
// ignores the extras at parse time.
export type StatusPageComponent = {
  id: string;
  name: string;
  /**
   * Free-form per-component string. A service URL on real services (e.g.
   * "https://us.api.mondoo.com"), a human note on the deployment markers
   * ("Incident-banner targeting for the 'mondoo-us' deployment (app#2457)"),
   * or null. Not used by targeting.
   */
  description: string | null;
  status: string;
  group_id: string | null;
  /** True for group containers; their child ids live in `components`. */
  group: boolean;
  /** Present (and meaningful) only when group === true. */
  components?: string[];
};

export type StatusPageAffectedComponent = {
  id: string;
  name: string;
};

export type StatusPageIncident = {
  id: string;
  name: string;
  /** e.g. "investigating" | "identified" | "monitoring" | "resolved" | "postmortem". */
  status: string;
  impact: IncidentImpact;
  /** Components this incident affects. Statuspage omits this on some entries. */
  components?: StatusPageAffectedComponent[];
};

export type StatusPageMaintenance = {
  id: string;
  name: string;
  /** e.g. "scheduled" | "in_progress" | "verifying" | "completed". */
  status: string;
  impact: IncidentImpact; // typically "maintenance"
  components?: StatusPageAffectedComponent[];
};

export type SummaryResponse = {
  page: {
    id: string;
    name: string;
    url: string;
    time_zone: string;
    updated_at: string;
  };
  status: {
    description: string;
    indicator: IncidentIndicator;
  };
  components: StatusPageComponent[];
  incidents: StatusPageIncident[];
  scheduled_maintenances: StatusPageMaintenance[];
};

/** The shape the banner consumes. Single canonical definition (re-exported by the hook). */
export type IncidentState = {
  msg: string;
  open: boolean;
  indicator: IncidentIndicator;
  fetched: boolean;
};

type MatchedIncident = {
  name: string;
  indicator: IncidentIndicator;
};

// Exhaustive map keeps impact → indicator total at compile time (no default
// branch, no casts). A new IncidentImpact member would fail to compile here.
const IMPACT_TO_INDICATOR: Record<IncidentImpact, IncidentIndicator> = {
  none: IncidentIndicator.NONE,
  maintenance: IncidentIndicator.MAINTENANCE,
  minor: IncidentIndicator.MINOR,
  major: IncidentIndicator.MAJOR,
  critical: IncidentIndicator.CRITICAL,
};

// Severity precedence: critical > major > minor > maintenance > none. Used to
// pick the dominant incident when several touch a watched component.
const INDICATOR_RANK: Record<IncidentIndicator, number> = {
  [IncidentIndicator.NONE]: 0,
  [IncidentIndicator.MAINTENANCE]: 1,
  [IncidentIndicator.MINOR]: 2,
  [IncidentIndicator.MAJOR]: 3,
  [IncidentIndicator.CRITICAL]: 4,
};

// Incidents in these states are no longer active. summary.json generally omits
// resolved ones, but we filter defensively in case the feed includes them.
const INACTIVE_STATUSES: ReadonlySet<string> = new Set([
  "resolved",
  "completed",
  "postmortem",
]);

export function impactToIndicator(impact: IncidentImpact): IncidentIndicator {
  return IMPACT_TO_INDICATOR[impact];
}

/**
 * Parse the comma-separated VITE_INCIDENT_COMPONENTS allowlist into trimmed,
 * non-empty entries. Entries may be component names or ids (resolved later), and
 * may carry an optional `REGION:` prefix (parsed by parseAllowlistEntry).
 */
export function parseIncidentComponents(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/** A single allowlist entry, optionally scoped to a region. */
export type ParsedAllowlistEntry = {
  /** Region the entry applies to (case-insensitive), or null for all regions. */
  region: string | null;
  /** Component name or id to watch. */
  component: string;
};

/**
 * Split an allowlist entry into an optional `REGION:` prefix and the component
 * name/id: `US:mondoo-us` → { region: "US", component: "mondoo-us" }; `global`
 * → { region: null, component: "global" }. The multi-region SaaS console serves
 * both regions from one bundle, so it binds a region marker to a region this
 * way (e.g. "US:mondoo-us,EU:mondoo-eu,global") and the active region decides
 * which scoped entries apply.
 *
 * A colon is RESERVED as the region delimiter: Statuspage component ids are
 * alphanumeric and our marker names (mondoo-us, private-aln, global) contain
 * none, so splitting on the first colon is unambiguous. The allowlist is
 * operator-controlled config set per deployment, so this invariant is
 * guaranteed at the source — a component identifier must not contain a colon or
 * its prefix would be misread as a region. An empty region side ("global" or
 * ":global") is treated as unscoped.
 */
export function parseAllowlistEntry(entry: string): ParsedAllowlistEntry {
  const colon = entry.indexOf(":");
  if (colon === -1) return { region: null, component: entry.trim() };
  const region = entry.slice(0, colon).trim();
  const component = entry.slice(colon + 1).trim();
  return { region: region.length > 0 ? region : null, component };
}

/**
 * Resolve allowlist entries (names or ids) to the set of component ids to watch.
 * A component is watched when its id OR name equals an allowlist entry
 * (case-insensitive, trimmed). Group containers are skipped — incidents attach
 * to leaf components, and a group's children match on their own. Entries that
 * match nothing in the feed (e.g. a private component not yet created in
 * Statuspage) simply contribute nothing.
 *
 * Region scoping: an entry with a `REGION:` prefix is only watched when it
 * matches `activeRegion` (case-insensitive); un-prefixed entries are always
 * watched. When `activeRegion` is undefined (pre-login, or a single-region
 * deployment) only un-prefixed entries apply, so existing un-prefixed
 * deployment values behave exactly as before.
 */
export function resolveWatchedComponentIds(
  components: StatusPageComponent[],
  allowlistEntries: string[],
  activeRegion?: string,
): Set<string> {
  const watched = new Set<string>();
  if (allowlistEntries.length === 0) return watched;

  const normalizedRegion = activeRegion?.trim().toLowerCase();

  // Identifiers (lowercased component name/id) to watch after applying region
  // scoping. A scoped entry whose region doesn't match the active region is
  // dropped here so it can never match a component below.
  const watchedIdentifiers = new Set<string>();
  for (const entry of allowlistEntries) {
    const { region, component } = parseAllowlistEntry(entry);
    if (component.length === 0) continue;
    if (region !== null && region.toLowerCase() !== normalizedRegion) continue;
    watchedIdentifiers.add(component.toLowerCase());
  }
  if (watchedIdentifiers.size === 0) return watched;

  for (const component of components) {
    if (component.group) continue;
    const idMatch = watchedIdentifiers.has(component.id.trim().toLowerCase());
    const nameMatch = watchedIdentifiers.has(
      component.name.trim().toLowerCase(),
    );
    if (idMatch || nameMatch) {
      watched.add(component.id);
    }
  }

  return watched;
}

/**
 * Return active incidents + scheduled maintenances that touch a watched
 * component. Maintenances default to "maintenance" impact and incidents with
 * "none" impact default to "minor" so a real, active incident never maps to a
 * non-displaying indicator.
 */
export function selectActiveIncidents(
  summary: SummaryResponse,
  watched: Set<string>,
): MatchedIncident[] {
  const matched: MatchedIncident[] = [];

  const consider = (
    entry: StatusPageIncident | StatusPageMaintenance,
    fallbackImpact: IncidentImpact,
  ) => {
    if (INACTIVE_STATUSES.has(entry.status.toLowerCase())) return;
    const touches = (entry.components ?? []).some((component) =>
      watched.has(component.id),
    );
    if (!touches) return;
    const impact = entry.impact === "none" ? fallbackImpact : entry.impact;
    matched.push({ name: entry.name, indicator: impactToIndicator(impact) });
  };

  for (const incident of summary.incidents ?? []) consider(incident, "minor");
  for (const maintenance of summary.scheduled_maintenances ?? [])
    consider(maintenance, "maintenance");

  return matched;
}

type ComputeIncidentOpts = {
  /** This deployment's watched components (names or ids); empty = not configured. */
  allowlistEntries: string[];
  /**
   * The user's active region name (e.g. "US"/"EU"), used to resolve
   * region-scoped (`REGION:`) allowlist entries. Undefined pre-login or on a
   * single-region deployment — only un-prefixed entries apply then.
   */
  activeRegion?: string;
  /** Today's hardcoded message factory, used only on the fallback path. */
  formatFallbackMessage: (indicator: IncidentIndicator) => string;
};

/**
 * Derive the banner state from a summary.json payload.
 *
 * - Allowlist configured: show the highest-impact active incident that touches a
 *   watched component, using its real name. If none match, the banner stays
 *   hidden — a targeted deployment does NOT fall back to the global rollup, and
 *   the "global" deployment marker is NOT auto-watched. To surface org-wide
 *   incidents a deployment must list it explicitly, e.g.
 *   VITE_INCIDENT_COMPONENTS="mondoo-us,global".
 * - Allowlist empty (not configured): today's behavior — show iff the global
 *   status.indicator is not "none", with the hardcoded message.
 */
export function computeIncidentState(
  summary: SummaryResponse,
  opts: ComputeIncidentOpts,
): IncidentState {
  if (opts.allowlistEntries.length > 0) {
    const watched = resolveWatchedComponentIds(
      summary.components ?? [],
      opts.allowlistEntries,
      opts.activeRegion,
    );
    const matches = selectActiveIncidents(summary, watched);

    if (matches.length > 0) {
      // Highest impact wins; ties resolve to the first encountered (incidents
      // before maintenances, in feed order).
      const top = matches.reduce((best, current) =>
        INDICATOR_RANK[current.indicator] > INDICATOR_RANK[best.indicator]
          ? current
          : best,
      );
      return {
        msg: top.name,
        open: true,
        indicator: top.indicator,
        fetched: true,
      };
    }

    // Configured but nothing relevant to this deployment is active.
    return {
      msg: "",
      open: false,
      indicator: IncidentIndicator.NONE,
      fetched: true,
    };
  }

  // Fallback — show the global rollup. Prefer Statuspage's own human-readable
  // status.description (e.g. "Partial System Outage"); fall back to our
  // indicator-based message when the feed omits it or it's blank/whitespace.
  const indicator = summary.status.indicator;
  const open = indicator !== IncidentIndicator.NONE;
  const description = summary.status.description?.trim();
  return {
    msg: open ? description || opts.formatFallbackMessage(indicator) : "",
    open,
    indicator,
    fetched: true,
  };
}
